import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { PopoverModule } from 'primeng/popover';
import { TooltipModule } from 'primeng/tooltip';
import { BadgeModule } from 'primeng/badge';
import { DividerModule } from 'primeng/divider';
import { TaskFilterComponent } from './task-filter.component';
import { TaskListComponent } from './task-list.component';
import { TaskFormComponent } from './task-form.component';
import { TaskCommentsComponent } from './task-comments.component';
import { TaskManagementService } from '../../../services/task-management.service';
import { TimezoneService } from '../../../services/timezone.service';
import { UserService } from '../../../services/user.service';
import { ProjectService } from '../../../services/project.service';
import { SSOService } from '../../../services/sso.service';
import { ProjectStateService } from '../../../services/project-state.service';
import { 
  ProjectTask,
  TaskKanbanBoard,
  TaskFilter,
  TaskCreateUpdate,
  TaskComment,
  TaskCommentCreate,
  TaskStatus,
  TaskTag,
  UserDetail
} from '../../../models/task-management.models';
import { Subject, forkJoin, of } from 'rxjs';
import { takeUntil, finalize, catchError } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

interface TaskPanelConfig {
  showCreateButton?: boolean;
  showFilters?: boolean;
  showKanbanView?: boolean;
  allowTaskEdit?: boolean;
  allowTaskDelete?: boolean;
  allowComments?: boolean;
  defaultView?: 'list' | 'kanban';
  compactMode?: boolean;
  maxHeight?: string;
}

@Component({
  selector: 'app-task-panel',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    PopoverModule,
    TooltipModule,
    BadgeModule,
    DividerModule,
    TaskFilterComponent,
    TaskListComponent,
    TaskFormComponent,
    TaskCommentsComponent
  ],
  templateUrl: './task-panel.component.html',
  styleUrls: ['./task-panel.component.css']
})
export class TaskPanelComponent implements OnInit, OnDestroy {
  @Input() blockScheduleId!: number;
  @Input() projectId!: number; // Add projectId input
  @Input() projectTemplateId?: number;
  @Input() config: TaskPanelConfig = {
    showCreateButton: true,
    showFilters: true,
    showKanbanView: true,
    allowTaskEdit: true,
    allowTaskDelete: true,
    allowComments: true,
    defaultView: 'kanban',
    compactMode: false
  };
  @Input() initialFilters?: Partial<TaskFilter>;
  @Input() currentUser?: UserDetail;

  @Output() taskCreated = new EventEmitter<ProjectTask>();
  @Output() taskUpdated = new EventEmitter<ProjectTask>();
  @Output() taskDeleted = new EventEmitter<number>();
  @Output() taskSelected = new EventEmitter<ProjectTask>();

  // Task data
  filteredTasks: ProjectTask[] = [];
  kanbanBoard?: TaskKanbanBoard;
  totalTaskCount: number = 0;
  hasMoreTasks: boolean = false;

  // Metadata
  availableStatuses: TaskStatus[] = [];
  availableTags: TaskTag[] = [];
  availableUsers: UserDetail[] = [];
  availableParentTasks: ProjectTask[] = [];

  // UI state
  currentView: 'list' | 'kanban' = 'list';
  activeViewMode: 'normal' | 'tags' = 'normal';
  showFilters: boolean = false;
  currentFilters: TaskFilter = {};
  activeFiltersCount: number = 0;

  // Loading states
  loading: boolean = false;
  loadingMore: boolean = false;
  error?: string;

  // Dialog states
  showTaskForm: boolean = false;
  taskFormReadOnly: boolean = false;
  showComments: boolean = false;
  editingTask?: ProjectTask;
  taskFormDefaults?: Partial<TaskCreateUpdate>;
  submittingTask: boolean = false;
  submittingComment: boolean = false;

  // Comments
  selectedTaskId: number = 0;
  selectedTaskComments: TaskComment[] = [];

  private destroy$ = new Subject<void>();
  private currentPage = 1;
  private readonly pageSize = 20;

  constructor(
    private taskService: TaskManagementService,
    private timezoneService: TimezoneService,
    private userService: UserService,
    private projectService: ProjectService,
    private messageService: MessageService,
    private ssoService: SSOService,
    private cdr: ChangeDetectorRef,
    private projectStateService: ProjectStateService
  ) {
    // Set default configuration
    this.config = {
      showCreateButton: true,
      showFilters: true,
      showKanbanView: true,
      allowTaskEdit: true,
      allowTaskDelete: true,
      allowComments: true,
      defaultView: 'list',
      compactMode: false,
      maxHeight: '100%',
      ...this.config
    };
  }

  ngOnInit() {
    this.currentView = this.config.defaultView || 'list';
    
    if (this.initialFilters) {
      this.currentFilters = { ...this.initialFilters };
      this.updateActiveFiltersCount();
    }

    this.loadInitialData();

    this.projectStateService.pageSearch$.pipe(takeUntil(this.destroy$)).subscribe(search => {
      this.currentFilters = { ...this.currentFilters, searchText: search || undefined };
      this.loadTasks();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get panelStyles() {
    return {
      'max-height': this.config.maxHeight
    };
  }

  private loadInitialData() {
    this.loading = true;
    this.error = undefined;

    // Load metadata in parallel using forkJoin
    forkJoin({
      statuses: this.taskService.getAllTaskStatuses().pipe(
        catchError(error => { console.error('Error loading task statuses:', error); return of([] as TaskStatus[]); })
      ),
      tags: this.taskService.getAllTaskTags().pipe(
        catchError(error => { console.error('Error loading task tags:', error); return of([] as TaskTag[]); })
      ),
      users: this.userService.getProjectUserList(this.projectId).pipe(
        catchError(error => { console.error('Error loading users:', error); return of([] as any[]); })
      )
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        this.availableStatuses = result.statuses;
        this.availableTags = result.tags;
        this.availableUsers = (result.users || [])
          .filter(user => user.userId && user.isActive !== false)
          .map(user => ({
            userId: user.userId || 0,
            name: `${user.firstName} ${user.lastName}`.trim(),
            email: user.email,
            role: user.roleName
          }));
        this.cdr.markForCheck();

        // Load tasks after metadata is ready
        this.loadTasks();
      },
      error: (error) => {
        this.error = 'Failed to load task data. Please try again.';
        console.error('Error loading initial task data:', error);
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  loadTasks() {
    this.currentPage = 1;

    const filters: TaskFilter = {
      ...this.currentFilters,
      blockScheduleId: this.blockScheduleId,
      projectTemplateId: this.projectTemplateId
    };

    let taskObservable$;

    if (this.currentView === 'kanban') {
      if (!this.blockScheduleId || this.blockScheduleId === 0) {
        taskObservable$ = this.taskService.getProjectKanbanBoard(
          this.projectId,
          undefined,
          undefined,
          filters
        );
      } else {
        taskObservable$ = this.taskService.getProjectKanbanBoard(
          this.projectId,
          this.blockScheduleId,
          this.projectTemplateId,
          filters
        );
      }

      taskObservable$.pipe(takeUntil(this.destroy$)).subscribe({
        next: (kanbanBoard) => {
          this.kanbanBoard = kanbanBoard;
          this.filteredTasks = [];

          if (this.kanbanBoard) {
            this.processVirtualColumns();
          }

          const regularTaskCount = this.kanbanBoard?.statusColumns?.reduce((sum, col) => sum + col.tasks.length, 0) || 0;
          const virtualTaskCount = this.kanbanBoard?.virtualColumns?.reduce((sum, col) => sum + col.tasks.length, 0) || 0;
          this.totalTaskCount = regularTaskCount + virtualTaskCount;
          this.availableParentTasks = this.filteredTasks.filter(t => !t.parentTaskId);
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.error = 'Failed to load tasks. Please try again.';
          console.error('Error loading tasks:', error);
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
    } else {
      if (this.blockScheduleId && this.blockScheduleId > 0) {
        this.taskService.getTasksByBlockSchedule(this.blockScheduleId, filters)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (tasks) => {
              this.filteredTasks = tasks || [];
              this.totalTaskCount = tasks?.length || 0;
              this.hasMoreTasks = false;
              this.availableParentTasks = this.filteredTasks.filter(t => !t.parentTaskId);
              this.loading = false;
              this.cdr.markForCheck();
            },
            error: (error) => {
              this.error = 'Failed to load tasks. Please try again.';
              console.error('Error loading tasks:', error);
              this.loading = false;
              this.cdr.markForCheck();
            }
          });
      } else {
        this.filteredTasks = [];
        this.totalTaskCount = 0;
        this.hasMoreTasks = false;
        this.availableParentTasks = [];
        this.loading = false;
        this.cdr.markForCheck();
      }
    }
  }

  setActiveViewMode(mode: 'normal' | 'tags'): void {
    this.activeViewMode = mode;
    if (mode === 'tags') {
      const prev = this.currentView;
      this.currentView = 'kanban';
      this.loadTasks();
      this.currentView = prev;
    }
    this.cdr.markForCheck();
  }

  get tagKanbanColumns(): { tag: TaskTag; tasks: ProjectTask[] }[] {
    const allTasks: ProjectTask[] = [
      ...(this.kanbanBoard?.statusColumns?.flatMap(c => c.tasks) ?? []),
      ...(this.kanbanBoard?.virtualColumns?.flatMap(c => c.tasks) ?? []),
      ...(this.filteredTasks ?? [])
    ];
    const seen = new Set<number>();
    const uniqueTasks = allTasks.filter(t => {
      if (seen.has(t.taskId)) { return false; }
      seen.add(t.taskId);
      return true;
    });
    return this.availableTags
      .map(tag => ({
        tag,
        tasks: uniqueTasks.filter(t => t.tags?.some(tt => tt.tagId === tag.tagId))
      }))
      .filter(group => group.tasks.length > 0);
  }

  get hasTagViewContent(): boolean {
    return (this.kanbanBoard?.virtualColumns?.length ?? 0) > 0 || this.tagKanbanColumns.length > 0;
  }

  loadMoreTasks() {
    if (this.loadingMore || !this.hasMoreTasks || this.currentView === 'kanban') {
      return;
    }

    this.loadingMore = true;
    this.currentPage++;

    const filters: TaskFilter = {
      ...this.currentFilters,
      blockScheduleId: this.blockScheduleId,
      projectTemplateId: this.projectTemplateId
    };

    this.taskService.getTasksByBlockSchedule(filters.blockScheduleId || 0, filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (tasks) => {
          if (tasks) {
            this.filteredTasks = tasks;
            this.hasMoreTasks = false;
          }
          this.loadingMore = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading more tasks:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load more tasks'
          });
          this.currentPage--;
          this.loadingMore = false;
          this.cdr.markForCheck();
        }
      });
  }

  setView(view: 'list' | 'kanban') {
    if (this.currentView !== view) {
      this.currentView = view;
      this.loadTasks();
    }
  }

  toggleFilters() {
    this.showFilters = !this.showFilters;
  }

  onFiltersChanged(filters: TaskFilter) {
    this.currentFilters = { ...filters };
    this.updateActiveFiltersCount();
    this.loadTasks();
  }

  onClearFilters() {
    this.currentFilters = this.initialFilters ? { ...this.initialFilters } : {};
    this.updateActiveFiltersCount();
    this.loadTasks();
  }

  private updateActiveFiltersCount() {
    let count = 0;
    
    if (this.currentFilters.searchText?.trim()) count++;
    if (this.currentFilters.statusIds?.length) count++;
    if (this.currentFilters.tagIds?.length) count++;
    if (this.currentFilters.assignedToIds?.length) count++;
    if (this.currentFilters.endDateFrom || this.currentFilters.endDateTo) count++;
    if (this.currentFilters.statusIds?.length) count++;
    if (this.currentFilters.isOverdue !== undefined) count++;

    this.activeFiltersCount = count;
  }

  openCreateTaskDialog(defaults?: Partial<TaskCreateUpdate>) {
    this.editingTask = undefined;
    this.taskFormReadOnly = false;
    
    this.taskFormDefaults = {
      projectId: this.projectId,
      blockScheduleId: this.blockScheduleId && this.blockScheduleId > 0 ? this.blockScheduleId : 0, // Use 0 for project-level tasks
      ...(this.projectTemplateId ? { projectTemplateId: this.projectTemplateId } : {}),
      ...defaults
    };
    this.showTaskForm = true;
  }

  onTaskClick(task: ProjectTask) {
    // Open task details; read-only if edit not allowed
    if (!this.config.allowTaskEdit) {
      this.taskFormReadOnly = true;
      this.editingTask = task;
      this.taskFormDefaults = undefined;
      this.showTaskForm = true;
      return;
    }
    this.onTaskEdit(task);
  }

  onTaskEdit(task: ProjectTask) {
    if (!this.config.allowTaskEdit) {
      this.taskFormReadOnly = true;
      this.editingTask = task;
      this.taskFormDefaults = undefined;
      this.showTaskForm = true;
      return;
    }
    
    this.editingTask = task;
    this.taskFormDefaults = undefined;
    this.taskFormReadOnly = false;
    this.showTaskForm = true;
  }

  onTaskCreate() {
    this.editingTask = undefined;
    this.taskFormReadOnly = false;
    
    this.taskFormDefaults = {
      projectId: this.projectId,
      blockScheduleId: this.blockScheduleId && this.blockScheduleId > 0 ? this.blockScheduleId : 0, // Use 0 for project-level tasks
      ...(this.projectTemplateId ? { projectTemplateId: this.projectTemplateId } : {})
    };
    this.showTaskForm = true;
  }

  onTaskDelete(task: ProjectTask) {
    if (!this.config.allowTaskDelete) return;

    this.taskService.deleteTask(task.taskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Task deleted successfully'
          });
          this.taskDeleted.emit(task.taskId);
          this.loadTasks();
        },
        error: (error) => {
          console.error('Error deleting task:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete task'
          });
        }
      });
  }

  onTaskStatusChange(event: { task: ProjectTask; statusId: number }) {
    this.taskService.updateTaskStatus(event.task.taskId, { taskId: event.task.taskId, statusId: event.statusId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: response.message || 'Task status updated'
          });
          // Find and emit updated task
          const updatedTask = this.findTaskById(event.task.taskId);
          if (updatedTask) {
            updatedTask.statusId = event.statusId;
            this.taskUpdated.emit(updatedTask);
          }
          this.loadTasks();
        },
        error: (error) => {
          console.error('Error updating task status:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update task status'
          });
        }
      });
  }

  onTaskCommentsClick(task: ProjectTask) {
    if (!this.config.allowComments) return;

    this.selectedTaskId = task.taskId;
    this.loadTaskComments(task.taskId);
    this.showComments = true;
  }

  private loadTaskComments(taskId: number) {
    this.taskService.getTaskComments(taskId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (comments) => {
          this.selectedTaskComments = comments || [];
        },
        error: (error) => {
          console.error('Error loading task comments:', error);
          this.selectedTaskComments = [];
        }
      });
  }

  onTaskSubmit(taskData: TaskCreateUpdate) {
    this.submittingTask = true;

    const request = this.editingTask 
      ? this.taskService.updateTask(this.editingTask.taskId, taskData)
      : this.taskService.createTask(taskData);

    request.pipe(
      takeUntil(this.destroy$),
      finalize(() => this.submittingTask = false)
    ).subscribe({
      next: (response) => {
        const action = this.editingTask ? 'updated' : 'created';
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: response.message || `Task ${action} successfully`
        });

        if (this.editingTask) {
          this.taskUpdated.emit(this.editingTask);
        }

        // Reload tasks to refresh the view with latest data
        this.loadTasks();

        this.showTaskForm = false;
      },
      error: (error) => {
        console.error('Error submitting task:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to ${this.editingTask ? 'update' : 'create'} task`
        });
      }
    });
  }

  onTaskFormCancel() {
    this.showTaskForm = false;
    this.editingTask = undefined;
    this.taskFormDefaults = undefined;
    this.taskFormReadOnly = false;
  }

  onCommentSubmit(commentData: TaskCommentCreate) {
    const currentUser = this.ssoService.getCurrentUser();
    if (!currentUser?.userId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'User not authenticated. Please login again.'
      });
      return;
    }

    this.submittingComment = true;

    // Ensure taskId is set on the comment data
    commentData.taskId = this.selectedTaskId;

    this.taskService.addTaskComment(commentData, currentUser.userId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.submittingComment = false)
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Comment added successfully'
          });
          this.loadTaskComments(this.selectedTaskId);
        },
        error: (error) => {
          console.error('Error adding comment:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add comment'
          });
        }
      });
  }

  onCommentDelete(commentId: number) {
    this.taskService.deleteTaskComment(commentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Comment deleted successfully'
          });
          this.loadTaskComments(this.selectedTaskId);
        },
        error: (error) => {
          console.error('Error deleting comment:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete comment'
          });
        }
      });
  }

  onCommentsDialogHide() {
    this.selectedTaskId = 0;
    this.selectedTaskComments = [];
  }

  private findTaskById(taskId: number): ProjectTask | undefined {
    // Search in filtered tasks first
    let task = this.filteredTasks.find(t => t.taskId === taskId);
    if (task) return task;

    // If not found, search in kanban board regular columns
    if (this.kanbanBoard?.statusColumns) {
      for (const column of this.kanbanBoard.statusColumns) {
        task = column.tasks.find(t => t.taskId === taskId);
        if (task) return task;
      }
    }

    // If not found, search in virtual columns
    if (this.kanbanBoard?.virtualColumns) {
      for (const column of this.kanbanBoard.virtualColumns) {
        task = column.tasks.find(t => t.taskId === taskId);
        if (task) return task;
      }
    }

    return undefined;
  }

  private processVirtualColumns() {
    if (!this.kanbanBoard?.statusColumns) {
      return;
    }

    const overdueTasks: ProjectTask[] = [];
    const dueTodayTasks: ProjectTask[] = [];

    // Find open/active tasks that are overdue or due today
    for (const column of this.kanbanBoard.statusColumns) {
      // Check if this is NOT a completed/closed status (include all priority levels and active statuses)
      const isCompletedStatus = column.statusType?.toLowerCase().includes('completed') || 
                               column.statusType?.toLowerCase().includes('closed') ||
                               column.statusType?.toLowerCase().includes('done') ||
                               column.statusName?.toLowerCase().includes('completed') ||
                               column.statusName?.toLowerCase().includes('closed') ||
                               column.statusName?.toLowerCase().includes('done');
      
      if (!isCompletedStatus) {
        const tasksToRemove: ProjectTask[] = [];

        for (const task of column.tasks) {
          if (task.endDate) {
            if (this.timezoneService.isStoredDateOverdue(task.endDate)) {
              // Task is overdue
              overdueTasks.push(task);
              tasksToRemove.push(task);
            } else if (this.timezoneService.isStoredDateDueToday(task.endDate)) {
              // Task is due today
              dueTodayTasks.push(task);
              tasksToRemove.push(task);
            }
          }
        }

        // Remove moved tasks from original column
        tasksToRemove.forEach(taskToRemove => {
          const index = column.tasks.findIndex(t => t.taskId === taskToRemove.taskId);
          if (index > -1) {
            column.tasks.splice(index, 1);
          }
        });
        
        // Update task count
        column.taskCount = column.tasks.length;
      }
    }

    // Create virtual columns
    this.kanbanBoard.virtualColumns = [];

    if (overdueTasks.length > 0) {
      this.kanbanBoard.virtualColumns.push({
        statusId: -1,
        statusName: 'Overdue',
        statusColor: '#ed1c4d',
        statusType: 'overdue',
        sortOrder: -2,
        isVirtual: true,
        tasks: overdueTasks,
        taskCount: overdueTasks.length
      });
    }

    if (dueTodayTasks.length > 0) {
      this.kanbanBoard.virtualColumns.push({
        statusId: -2,
        statusName: 'Due Today',
        statusColor: '#e09500',
        statusType: 'due_today',
        sortOrder: -1,
        isVirtual: true,
        tasks: dueTodayTasks,
        taskCount: dueTodayTasks.length
      });
    }
  }

  getVirtualColumn(statusName: string) {
    return this.kanbanBoard?.virtualColumns?.find(col => col.statusName === statusName);
  }

  getDaysOverdue(endDate: Date | string): number {
    return this.timezoneService.getStoredDaysOverdue(endDate);
  }

  formatCount(count: number): string {
    return count.toString().padStart(2, '0');
  }

  formatCardDate(date: Date | string | undefined): string {
    if (!date) return '';
    return this.timezoneService.formatStoredDate(date) || '';
  }

  getTaskContextLabel(task: ProjectTask): string {
    return [
      task.skidName,
      task.templateShortCode || task.templateName,
      task.sectionName
    ]
      .map(value => (value || '').trim())
      .filter(Boolean)
      .join(' | ');
  }

  getVirtualColumnColor(statusName: string): string {
    return statusName === 'Overdue' ? '#ed1c4d' : '#e09500';
  }
}
